import React, { useState } from 'react';
import { PlusIcon, FunnelIcon, ArrowPathIcon, GiftIcon, UserGroupIcon, XMarkIcon } from '@heroicons/react/24/outline';

export const PromotionalManagement: React.FC = () => {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock data for referrals
  const referrals = [
    {
      id: 1,
      referrer: 'John Smith',
      referee: 'Sarah Johnson',
      date: '2024-03-15',
      status: 'completed',
      referrerBonus: 10,
      refereeDiscount: 15
    },
    // Add more mock data as needed
  ];

  // Add these state variables at the top of the component
  const [promotionalCodes, setPromotionalCodes] = useState([
    {
      id: 1,
      name: 'New User Discount',
      description: 'Welcome discount for new users',
      code: 'WELCOME2024',
      startDate: '2024-03-01',
      endDate: '2024-06-30',
      discount: 15,
      usageCount: 45,
      status: 'active'
    }
  ]);

  const [newPromotion, setNewPromotion] = useState({
    name: '',
    code: '',
    description: '',
    startDate: '',
    endDate: '',
    discount: ''
  });

  // Add this new state for managing referral programme form
  const [newReferral, setNewReferral] = useState({
    programmeName: '',
    programmeNumber: '',
    description: '',
    startDate: '',
    endDate: '',
    refereeDiscount: '',
    referrerBonus: ''
  });

  // Add this state for managing referral programmes
  const [referralProgrammes, setReferralProgrammes] = useState([
    {
      id: 1,
      referrer: 'John Smith',
      referee: 'Sarah Johnson',
      date: '2024-03-15',
      status: 'completed',
      referrerBonus: 10,
      refereeDiscount: 15
    }
  ]);

  // Add these new state variables at the top with other states
  const [selectedReport, setSelectedReport] = useState<string>('');
  const [selectedChartType, setSelectedChartType] = useState<string>('bar');
  const [showChartControls, setShowChartControls] = useState(false);

  // Add this function to handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewPromotion(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Add this function to handle form submission
  const handleCreateCode = () => {
    if (!newPromotion.name || !newPromotion.code || !newPromotion.discount) {
      alert('Please fill in all required fields');
      return;
    }

    const newCode = {
      id: Date.now(),
      name: newPromotion.name,
      description: newPromotion.description,
      code: newPromotion.code,
      startDate: newPromotion.startDate || new Date().toISOString().split('T')[0],
      endDate: newPromotion.endDate,
      discount: parseInt(newPromotion.discount),
      usageCount: 0,
      status: 'active'
    };

    setPromotionalCodes(prev => [...prev, newCode]);

    // Reset form
    setNewPromotion({
      name: '',
      code: '',
      description: '',
      startDate: '',
      endDate: '',
      discount: ''
    });
  };

  // Add this function to handle referral form input changes
  const handleReferralInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewReferral(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Add this function to handle referral programme creation
  const handleCreateProgramme = () => {
    if (!newReferral.programmeName || !newReferral.programmeNumber || !newReferral.refereeDiscount || !newReferral.referrerBonus) {
      alert('Please fill in all required fields');
      return;
    }

    const newProgramme = {
      id: Date.now(),
      referrer: 'New Referrer', // This would typically come from the logged-in user
      referee: 'Pending',
      date: new Date().toISOString().split('T')[0],
      status: 'active',
      referrerBonus: parseInt(newReferral.referrerBonus),
      refereeDiscount: parseInt(newReferral.refereeDiscount)
    };

    setReferralProgrammes(prev => [...prev, newProgramme]);

    // Reset form
    setNewReferral({
      programmeName: '',
      programmeNumber: '',
      description: '',
      startDate: '',
      endDate: '',
      refereeDiscount: '',
      referrerBonus: ''
    });
  };

  // Add this function to handle detailed report viewing
  const handleViewDetailedReport = (reportType: string) => {
    setSelectedReport(reportType);
    setShowChartControls(true);
    // Scroll to chart section
    document.getElementById('chart-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Promotional Code & Referrals Management</h1>
          <p className="mt-2 text-sm text-gray-600">Manage promotional codes and track referral activities</p>
        </div>

        {/* Section 1: Create Promotional Code */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-gray-900">Create Promotional Code</h2>
            <button 
              onClick={handleCreateCode}
              className="flex items-center bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] transition-colors"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Create Code
            </button>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Promotion Code Name
              </label>
              <input
                type="text"
                name="name"
                value={newPromotion.name}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                placeholder="Enter promotion name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Promotion Code Number
              </label>
              <input
                type="text"
                name="code"
                value={newPromotion.code}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                placeholder="e.g., WELCOME2024"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Promotion Code Description
              </label>
              <textarea
                name="description"
                value={newPromotion.description}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                rows={3}
                placeholder="Enter promotion description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Validity Period
              </label>
              <div className="flex space-x-4">
                <input
                  type="date"
                  name="startDate"
                  value={newPromotion.startDate}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                  min={new Date().toISOString().split('T')[0]}
                />
                <input
                  type="date"
                  name="endDate"
                  value={newPromotion.endDate}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                  min={newPromotion.startDate || new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Percentage
              </label>
              <input
                type="number"
                name="discount"
                value={newPromotion.discount}
                onChange={handleInputChange}
                min="0"
                max="100"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                placeholder="Enter discount percentage"
              />
            </div>
          </div>
        </div>

        {/* Section 2: Promotional Code Review */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-gray-900">Promotional Code Review</h2>
            <div className="flex space-x-4">
              <select
                className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value)}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
              <select
                className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="upcoming">Upcoming</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Validity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {promotionalCodes.map((code) => (
                  <tr key={code.id}>
                    <td className="px-4 py-4 text-sm text-gray-900">{code.code}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">{code.name}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">{code.discount}%</td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {code.startDate} - {code.endDate}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">{code.usageCount} times</td>
                    <td className="px-4 py-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        code.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {code.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 3: Create Referrals */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-gray-900">Create Referrals Programme</h2>
            <button 
              onClick={handleCreateProgramme}
              className="flex items-center bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] transition-colors"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Create Programme
            </button>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Referrals Programme Name
              </label>
              <input
                type="text"
                name="programmeName"
                value={newReferral.programmeName}
                onChange={handleReferralInputChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                placeholder="Enter programme name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Referrals Programme Number
              </label>
              <input
                type="text"
                name="programmeNumber"
                value={newReferral.programmeNumber}
                onChange={handleReferralInputChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                placeholder="e.g., REF2024"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Referrals Programme Description
              </label>
              <textarea
                name="description"
                value={newReferral.description}
                onChange={handleReferralInputChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                rows={3}
                placeholder="Enter programme description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Validity Period
              </label>
              <div className="flex space-x-4">
                <input
                  type="date"
                  name="startDate"
                  value={newReferral.startDate}
                  onChange={handleReferralInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                  min={new Date().toISOString().split('T')[0]}
                />
                <input
                  type="date"
                  name="endDate"
                  value={newReferral.endDate}
                  onChange={handleReferralInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                  min={newReferral.startDate || new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Referee Discount (%)
                </label>
                <input
                  type="number"
                  name="refereeDiscount"
                  value={newReferral.refereeDiscount}
                  onChange={handleReferralInputChange}
                  min="0"
                  max="100"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                  placeholder="e.g., 15"
                />
                <p className="mt-1 text-xs text-gray-500">Discount for new referred users</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Referrer Bonus (%)
                </label>
                <input
                  type="number"
                  name="referrerBonus"
                  value={newReferral.referrerBonus}
                  onChange={handleReferralInputChange}
                  min="0"
                  max="100"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                  placeholder="e.g., 10"
                />
                <p className="mt-1 text-xs text-gray-500">Reward for existing users</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Referrals Review */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-gray-900">Referrals Review</h2>
            <div className="flex items-center space-x-4">
              <div className="bg-gray-100 p-3 rounded-lg">
                <span className="text-sm font-medium text-gray-700">Default Referral Rewards:</span>
                <div className="mt-1">
                  <span className="text-sm text-gray-600">Referrer: 10% | Referee: 15%</span>
                </div>
              </div>
              <button className="flex items-center text-[#FF5722] hover:text-[#F4511E]">
                <ArrowPathIcon className="w-5 h-5 mr-1" />
                Update Rates
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referrer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referee</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rewards</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {referralProgrammes.map((referral) => (
                  <tr key={referral.id}>
                    <td className="px-4 py-4 text-sm text-gray-900">{referral.referrer}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">{referral.referee}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">{referral.date}</td>
                    <td className="px-4 py-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        referral.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {referral.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      Referrer: {referral.referrerBonus}% | Referee: {referral.refereeDiscount}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 5: Financial Reports */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-gray-900">Financial Reports</h2>
            <div className="flex items-center space-x-4">
              <select
                className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                defaultValue="last30"
              >
                <option value="last7">Last 7 days</option>
                <option value="last30">Last 30 days</option>
                <option value="last90">Last 90 days</option>
                <option value="thisYear">This Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Promotional Code Discounts */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Promotional Code Discounts</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Amount</span>
                  <span className="text-lg font-bold text-gray-900">$2,450.00</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Number of Uses</span>
                  <span className="text-lg font-bold text-gray-900">156</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Average Discount</span>
                  <span className="text-lg font-bold text-gray-900">$15.70</span>
                </div>
                <button 
                  onClick={() => handleViewDetailedReport('promo')}
                  className="w-full mt-2 text-[#FF5722] text-sm hover:text-[#F4511E] flex items-center justify-center"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-1" />
                  View Detailed Report
                </button>
              </div>
            </div>

            {/* Referrer Discounts */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Referrer Bonuses</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Amount</span>
                  <span className="text-lg font-bold text-gray-900">$1,850.00</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Number of Referrers</span>
                  <span className="text-lg font-bold text-gray-900">92</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Average Bonus</span>
                  <span className="text-lg font-bold text-gray-900">$20.10</span>
                </div>
                <button 
                  onClick={() => handleViewDetailedReport('referrer')}
                  className="w-full mt-2 text-[#FF5722] text-sm hover:text-[#F4511E] flex items-center justify-center"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-1" />
                  View Detailed Report
                </button>
              </div>
            </div>

            {/* Referee Discounts */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Referee Discounts</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Amount</span>
                  <span className="text-lg font-bold text-gray-900">$2,150.00</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Number of Referees</span>
                  <span className="text-lg font-bold text-gray-900">108</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Average Discount</span>
                  <span className="text-lg font-bold text-gray-900">$19.90</span>
                </div>
                <button 
                  onClick={() => handleViewDetailedReport('referee')}
                  className="w-full mt-2 text-[#FF5722] text-sm hover:text-[#F4511E] flex items-center justify-center"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-1" />
                  View Detailed Report
                </button>
              </div>
            </div>
          </div>

          {/* Summary Chart */}
          <div id="chart-section" className="mt-8 p-4 border border-gray-200 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-gray-700">
                {selectedReport === 'promo' && 'Promotional Code Analysis'}
                {selectedReport === 'referrer' && 'Referrer Bonus Analysis'}
                {selectedReport === 'referee' && 'Referee Discount Analysis'}
                {!selectedReport && 'Discount Distribution'}
              </h3>
              <div className="flex space-x-4 items-center">
                {showChartControls && (
                  <>
                    <select
                      value={selectedChartType}
                      onChange={(e) => setSelectedChartType(e.target.value)}
                      className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                    >
                      <option value="bar">Bar Chart</option>
                      <option value="line">Line Chart</option>
                      <option value="pie">Pie Chart</option>
                      <option value="area">Area Chart</option>
                    </select>
                    
                    <select
                      className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                      defaultValue="daily"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>

                    <button
                      onClick={() => {
                        setSelectedReport('');
                        setShowChartControls(false);
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {showChartControls ? (
              <div className="space-y-4">
                {/* Chart Filters */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-3 gap-4">
                    {selectedReport === 'promo' && (
                      <>
                        <select className="p-2 border border-gray-300 rounded-md">
                          <option value="all">All Codes</option>
                          <option value="active">Active Codes</option>
                          <option value="expired">Expired Codes</option>
                        </select>
                        <select className="p-2 border border-gray-300 rounded-md">
                          <option value="all">All Discounts</option>
                          <option value="high">&gt;50% Discount</option>
                          <option value="medium">25-50% Discount</option>
                          <option value="low">&lt;25% Discount</option>
                        </select>
                        <select className="p-2 border border-gray-300 rounded-md">
                          <option value="usage">By Usage</option>
                          <option value="revenue">By Revenue</option>
                          <option value="average">By Average Value</option>
                        </select>
                      </>
                    )}
                    {selectedReport === 'referrer' && (
                      <>
                        <select className="p-2 border border-gray-300 rounded-md">
                          <option value="all">All Referrers</option>
                          <option value="top">Top Referrers</option>
                          <option value="new">New Referrers</option>
                        </select>
                        <select className="p-2 border border-gray-300 rounded-md">
                          <option value="all">All Bonuses</option>
                          <option value="high">&gt;$50 Bonus</option>
                          <option value="medium">$25-$50 Bonus</option>
                          <option value="low">&lt;$25 Bonus</option>
                        </select>
                        <select className="p-2 border border-gray-300 rounded-md">
                          <option value="count">By Count</option>
                          <option value="amount">By Amount</option>
                          <option value="success">By Success Rate</option>
                        </select>
                      </>
                    )}
                    {selectedReport === 'referee' && (
                      <>
                        <select className="p-2 border border-gray-300 rounded-md">
                          <option value="all">All Referees</option>
                          <option value="converted">Converted</option>
                          <option value="pending">Pending</option>
                        </select>
                        <select className="p-2 border border-gray-300 rounded-md">
                          <option value="all">All Discounts</option>
                          <option value="high">&gt;$50 Discount</option>
                          <option value="medium">$25-$50 Discount</option>
                          <option value="low">&lt;$25 Discount</option>
                        </select>
                        <select className="p-2 border border-gray-300 rounded-md">
                          <option value="value">By Value</option>
                          <option value="frequency">By Frequency</option>
                          <option value="retention">By Retention</option>
                        </select>
                      </>
                    )}
                  </div>
                </div>

                {/* Chart Area */}
                <div className="h-64 bg-gray-50 rounded flex items-center justify-center">
                  <span className="text-gray-500">
                    {selectedChartType} Chart for {selectedReport === 'promo' ? 'Promotional Codes' : 
                      selectedReport === 'referrer' ? 'Referrer Bonuses' : 'Referee Discounts'}
                  </span>
                </div>
              </div>
            ) : (
              // Original chart content when no detailed report is selected
              <div className="h-64 bg-gray-50 rounded flex items-center justify-center">
                <span className="text-gray-500">Select "View Detailed Report" from any card above</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromotionalManagement; 